/**
 * @name useModalDependenteDetalhe
 * @author Gabriel M. Guimarães | gabrielmg7
 * @description Hook para buscar dados reais do dependente para o modal de detalhes
 * @package components/pages/gestao-financeira/dependentes/components
 */

import { useCallback, useEffect, useState } from 'react';
import { useAlert, useUserContext } from '../../../../../contexts';
import { useMultiTenantServices } from '../../../../../services/supabase/entities/useMultiTenantServices';
import { viewHistoricoVendasAluno } from '../../../../../services/supabase/views/ViewHistoricoVendasAluno';
import { viewExtratoConsumoAluno } from '../../../../../services/supabase/views/ViewExtratoConsumoAluno';
import { viewRestricoesAlimentares } from '../../../../../services/supabase/views/ViewRestricoesAlimentares';
import { viewSaldosAlunos } from '../../../../../services/supabase/views/ViewSaldosAlunos';
import { IPessoas } from '../../../../../types/tables/IPessoas';
import { IVwHistoricoVendasAluno } from '../../../../../types/views/IVwHistoricoVendasAluno';
import { IVwSaldosAlunos } from '../../../../../types/views/IVwSaldosAlunos';
import { DependenteComSaldo } from '../useDependentesData';

export interface DependenteDetalhado extends IVwSaldosAlunos {
     // Dados pessoais completos
     dadosPessoais: IPessoas | null;
     
     // Métricas calculadas
     progressoGasto: number;
     statusSaldo: 'alto' | 'medio' | 'baixo' | 'zerado';
     
     // Histórico de compras
     comprasRecentes: IVwHistoricoVendasAluno[];
     
     // Estatísticas
     totalComprasRecentes: number;
     valorMedioCompra: number;
     
     // Restrições e configurações
     restricoes: string[];
     ultimaAtividade?: Date;

     extratosRecentes: any[];
}

/**
 * Hook para buscar dados completos do dependente
 */
const useModalDependenteDetalhe = (dependente: DependenteComSaldo | null) => {
     const { userData } = useUserContext();
     const { showError } = useAlert();
     const services = useMultiTenantServices();
     
     // Estados
     const [dependenteDetalhado, setDependenteDetalhado] = useState<DependenteDetalhado | null>(null);
     const [loading, setLoading] = useState(false);
     const [error, setError] = useState<string | null>(null);
     const [lastDependenteId, setLastDependenteId] = useState<number | null>(null);

     /**
      * Busca dados completos do dependente
      */
     const buscarDadosCompletos = useCallback(async (dependenteData: DependenteComSaldo) => {
          if (!userData?.fk_id_organizacao || !dependenteData.id_aluno) return;

          setLoading(true);
          setError(null);

          try {
               // Buscar dados pessoais do aluno
               const pessoasResponse = await services.pessoas.getById(dependenteData.id_aluno);
               const dadosPessoais = pessoasResponse.data || null;

               // Buscar saldo atualizado do aluno
               const saldoResponse = await viewSaldosAlunos.getSaldoPorAluno(userData.fk_id_organizacao, dependenteData.id_aluno);
               const saldoAtualizado = saldoResponse.data || dependenteData;

               // Buscar histórico de compras (últimas 10)
               const historicoResponse = await viewHistoricoVendasAluno.getHistoricoPorAluno(userData.fk_id_organizacao, dependenteData.id_aluno, 10);
               const comprasRecentes = Array.isArray(historicoResponse.data) 
                    ? historicoResponse.data.flat() // Achata o array se for array de arrays
                    : [];

               const historicoConsumoAluno = await viewExtratoConsumoAluno.getExtratoPorAluno(userData.fk_id_organizacao, dependenteData.id_aluno, 10);
               const extratosRecentes = Array.isArray(historicoConsumoAluno.data)
                    ? historicoConsumoAluno.data.flat()
                    : [];

               // Buscar restrições alimentares reais do dependente na view
               let restricoes: string[] = [];
               try {
                    const restricoesResponse = await viewRestricoesAlimentares.getAll(
                         userData?.fk_id_organizacao,
                         0, // page 0 para não pular registros
                         100,
                         undefined,
                         {
                              items: [
                                   {
                                        field: 'fk_id_aluno',
                                        operator: 'equals',
                                        value: dependenteData.id_aluno,
                                   },
                              ],
                         }
                    );
                    if (Array.isArray(restricoesResponse.data)) {
                         restricoes = restricoesResponse.data
                              .filter((r: any) => r.fk_id_aluno === dependenteData.id_aluno && r.produto_descricao)
                              .map((r: any) => String(r.produto_descricao));
                    }
               } catch (e) {
                    restricoes = [];
               }

               // Calcular métricas - tratando o caso onde saldoAtualizado pode ser array ou objeto
               const saldoData = Array.isArray(saldoAtualizado) ? saldoAtualizado[0] : saldoAtualizado;
               const saldoAtual = Number(saldoData?.saldo_atual) || 0;
               const limiteGasto = Number(saldoData?.limite_diario) || 50; // fallback para R$ 50
               const progressoGasto = limiteGasto > 0 ? (saldoAtual / limiteGasto) * 100 : 0;

               // Determinar status do saldo
               const statusSaldo = (() => {
                    if (saldoAtual === 0) return 'zerado';
                    if (saldoAtual < 5) return 'baixo';
                    if (saldoAtual < 20) return 'medio';
                    return 'alto';
               })() as 'alto' | 'medio' | 'baixo' | 'zerado';

               // Calcular valor médio das compras
               const valorMedioCompra = comprasRecentes.length > 0 
                    ? comprasRecentes.reduce((acc, compra) => acc + (Number(compra.valor_final) || 0), 0) / comprasRecentes.length
                    : 0;

               // Usar dados reais ou dados básicos do dependente
               const saldoDataFinal = Array.isArray(saldoData) ? saldoData[0] : saldoData;
               const saldoAtualFinal = saldoDataFinal || dependenteData;

               // Montar objeto completo
               const dependenteCompleto: DependenteDetalhado = {
                    ...saldoAtualFinal,
                    dadosPessoais,
                    progressoGasto,
                    statusSaldo,
                    comprasRecentes,
                    extratosRecentes,
                    totalComprasRecentes: comprasRecentes.length,
                    valorMedioCompra,
                    restricoes,
                    ultimaAtividade: comprasRecentes.length > 0 && comprasRecentes[0]?.data_venda ? new Date(comprasRecentes[0].data_venda) : undefined,
               };

               setDependenteDetalhado(dependenteCompleto);

          } catch (error: any) {
               console.error('Erro ao buscar dados do dependente:', error);
               const errorMessage = error.message || 'Erro inesperado ao carregar dados do dependente';
               setError(errorMessage);
               showError(errorMessage);
          } finally {
               setLoading(false);
          }
     }, [userData?.fk_id_organizacao, services.pessoas, showError]); // Manter apenas a dependência essencial

     /**
      * Recarrega dados do dependente
      */
     const recarregarDados = useCallback(async () => {
          if (dependente) {
               await buscarDadosCompletos(dependente);
          }
     }, [dependente, buscarDadosCompletos]); // Manter apenas a dependência essencial

     /**
      * Limpa dados quando o modal é fechado
      */
     const limparDados = useCallback(() => {
          setDependenteDetalhado(null);
          setError(null);
     }, []);

     // Carregar dados quando o dependente muda
     useEffect(() => {
          if (dependente && dependente.id_aluno !== lastDependenteId) {
               setLastDependenteId(dependente.id_aluno);
               buscarDadosCompletos(dependente);
          } else if (!dependente && lastDependenteId !== null) {
               setLastDependenteId(null);
               limparDados();
          }
     }, [dependente?.id_aluno]); // Usar apenas o ID como dependência

     return {
          // Estados
          dependenteDetalhado,
          loading,
          error,
          
          // Ações
          recarregarDados,
          limparDados,
          
          // Utilitários
          formatCurrency: (value: number | null) => {
               if (value === null || value === undefined) return 'R$ 0,00';
               return new Intl.NumberFormat('pt-BR', {
                    style: 'currency',
                    currency: 'BRL',
               }).format(value);
          },
          
          formatDate: (date: string | Date | null) => {
               if (!date) return 'Não informado';
               const dateObj = typeof date === 'string' ? new Date(date) : date;
               return dateObj.toLocaleDateString('pt-BR', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
               });
          },
          
          getStatusColor: (status: 'alto' | 'medio' | 'baixo' | 'zerado') => {
               switch (status) {
                    case 'alto': return 'success';
                    case 'medio': return 'warning';
                    case 'baixo': return 'error';
                    case 'zerado': return 'default';
                    default: return 'default';
               }
          },
     };
};

export default useModalDependenteDetalhe;
